// Storage Cleanup Service
// Handles bulk deletion of posts, messages, and media files

import { 
  collection, 
  getDocs, 
  deleteDoc, 
  doc, 
  writeBatch,
  query,
  where
} from 'firebase/firestore';
import { db } from '../firebase';
import { cloudinaryService } from './cloudinary';
import { storageUsageService } from './storageUsageService';

export interface CleanupResult {
  success: boolean;
  deleted: {
    posts: number;
    messages: number;
    profilePictures: number;
    events: number;
    cloudinaryImages: number;
  };
  errors: string[];
}

class StorageCleanupService {
  private static instance: StorageCleanupService;

  private constructor() {}

  public static getInstance(): StorageCleanupService {
    if (!StorageCleanupService.instance) {
      StorageCleanupService.instance = new StorageCleanupService();
    }
    return StorageCleanupService.instance;
  }

  /**
   * Clear all storage: posts, messages, and media files for an organization
   */
  public async clearAllStorage(organizationCode: string): Promise<CleanupResult> {
    const result: CleanupResult = {
      success: true,
      deleted: {
        posts: 0,
        messages: 0,
        profilePictures: 0,
        events: 0,
        cloudinaryImages: 0,
      },
      errors: [],
    };

    try {
      console.log(`🧹 Starting storage cleanup for organization: ${organizationCode}...`);

      // 1. Delete all posts and their images for this organization
      result.deleted.posts = await this.deleteAllPosts(organizationCode);

      // 2. Delete all messages and their media for this organization
      result.deleted.messages = await this.deleteAllMessages(organizationCode);

      // 3. Delete event images for this organization
      result.deleted.events = await this.deleteAllEventImages(organizationCode);

      // 4. Clear profile pictures (keep default placeholders) for this organization
      result.deleted.profilePictures = await this.clearProfilePictures(organizationCode);

      // 5. Delete Cloudinary images for this organization
      result.deleted.cloudinaryImages = await this.deleteCloudinaryImages(organizationCode);

      // Reset storage usage tracking for this organization
      await storageUsageService.resetUsage(organizationCode);

      console.log('✅ Storage cleanup completed:', result);
      return result;
    } catch (error) {
      console.error('❌ Error during storage cleanup:', error);
      result.success = false;
      result.errors.push(`Cleanup failed: ${error}`);
      return result;
    }
  }

  /**
   * Delete all posts and their associated images for an organization
   */
  private async deleteAllPosts(organizationCode: string): Promise<number> {
    try {
      const postsSnapshot = await getDocs(
        query(collection(db, 'posts'), where('organizationCode', '==', organizationCode))
      );
      let deletedCount = 0;
      const batchSize = 500;
      let batch = writeBatch(db);
      let operationCount = 0;

      for (const postDoc of postsSnapshot.docs) {
        const postData = postDoc.data();
        
        // Try to delete Cloudinary image if exists
        if (postData.imageUrl && postData.imageUrl.includes('cloudinary.com')) {
          try {
            const publicId = this.extractCloudinaryPublicId(postData.imageUrl);
            if (publicId) {
              await cloudinaryService.deleteImage(publicId, organizationCode);
            }
          } catch (error) {
            console.warn('Failed to delete Cloudinary image for post:', postDoc.id);
          }
        }

        batch.delete(postDoc.ref);
        operationCount++;
        deletedCount++;

        if (operationCount >= batchSize) {
          await batch.commit();
          batch = writeBatch(db);
          operationCount = 0;
        }
      }

      if (operationCount > 0) {
        await batch.commit();
      }

      // Also delete comments
      const commentsSnapshot = await getDocs(collection(db, 'comments'));
      batch = writeBatch(db);
      operationCount = 0;

      for (const commentDoc of commentsSnapshot.docs) {
        batch.delete(commentDoc.ref);
        operationCount++;

        if (operationCount >= batchSize) {
          await batch.commit();
          batch = writeBatch(db);
          operationCount = 0;
        }
      }

      if (operationCount > 0) {
        await batch.commit();
      }

      console.log(`✅ Deleted ${deletedCount} posts`);
      return deletedCount;
    } catch (error) {
      console.error('Error deleting posts:', error);
      return 0;
    }
  }

  /**
   * Delete all messages and their media for an organization
   */
  private async deleteAllMessages(organizationCode: string): Promise<number> {
    try {
      const conversationsSnapshot = await getDocs(
        query(collection(db, 'conversations'), where('organizationCode', '==', organizationCode))
      );
      let deletedCount = 0;
      const batchSize = 500;

      for (const convDoc of conversationsSnapshot.docs) {
        const messagesSnapshot = await getDocs(
          collection(db, 'conversations', convDoc.id, 'messages')
        );

        let batch = writeBatch(db);
        let operationCount = 0;

        for (const msgDoc of messagesSnapshot.docs) {
          const msgData = msgDoc.data();
          
          // Try to delete Cloudinary image if exists
          if (msgData.imageUrl && msgData.imageUrl.includes('cloudinary.com')) {
            try {
              const publicId = this.extractCloudinaryPublicId(msgData.imageUrl);
              if (publicId) {
                await cloudinaryService.deleteImage(publicId, organizationCode);
              }
            } catch (error) {
              console.warn('Failed to delete Cloudinary image for message:', msgDoc.id);
            }
          }

          batch.delete(msgDoc.ref);
          operationCount++;
          deletedCount++;

          if (operationCount >= batchSize) {
            await batch.commit();
            batch = writeBatch(db);
            operationCount = 0;
          }
        }

        if (operationCount > 0) {
          await batch.commit();
        }

        // Delete conversation document
        await deleteDoc(doc(db, 'conversations', convDoc.id));
      }

      console.log(`✅ Deleted ${deletedCount} messages`);
      return deletedCount;
    } catch (error) {
      console.error('Error deleting messages:', error);
      return 0;
    }
  }

  /**
   * Delete all event images for an organization
   */
  private async deleteAllEventImages(organizationCode: string): Promise<number> {
    try {
      const eventsSnapshot = await getDocs(
        query(collection(db, 'events'), where('organizationCode', '==', organizationCode))
      );
      let deletedCount = 0;

      for (const eventDoc of eventsSnapshot.docs) {
        const eventData = eventDoc.data();
        
        // Try to delete Cloudinary image if exists
        if (eventData.imageUrl && eventData.imageUrl.includes('cloudinary.com')) {
          try {
            const publicId = this.extractCloudinaryPublicId(eventData.imageUrl);
            if (publicId) {
              await cloudinaryService.deleteImage(publicId, organizationCode);
              deletedCount++;
            }
          } catch (error) {
            console.warn('Failed to delete Cloudinary image for event:', eventDoc.id);
          }
        }

        // Clear imageUrl from event (keep event, just remove image)
        await deleteDoc(doc(db, 'events', eventDoc.id));
      }

      console.log(`✅ Deleted ${deletedCount} event images`);
      return deletedCount;
    } catch (error) {
      console.error('Error deleting event images:', error);
      return 0;
    }
  }

  /**
   * Clear profile pictures (set to null, keep default placeholders) for an organization
   */
  private async clearProfilePictures(organizationCode: string): Promise<number> {
    try {
      const usersSnapshot = await getDocs(
        query(
          collection(db, 'users'),
          where('organizationCode', '==', organizationCode),
          where('profilePictureUrl', '!=', null)
        )
      );
      let clearedCount = 0;
      const batchSize = 500;
      let batch = writeBatch(db);
      let operationCount = 0;

      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        
        // Try to delete Cloudinary image if exists
        if (userData.profilePictureUrl && userData.profilePictureUrl.includes('cloudinary.com')) {
          try {
            const publicId = this.extractCloudinaryPublicId(userData.profilePictureUrl);
            if (publicId) {
              await cloudinaryService.deleteImage(publicId, organizationCode);
            }
          } catch (error) {
            console.warn('Failed to delete Cloudinary image for profile:', userDoc.id);
          }
        }

        // Clear profile picture URL
        batch.update(userDoc.ref, {
          profilePictureUrl: null,
          profilePictureUpdatedAt: null,
        });
        operationCount++;
        clearedCount++;

        if (operationCount >= batchSize) {
          await batch.commit();
          batch = writeBatch(db);
          operationCount = 0;
        }
      }

      if (operationCount > 0) {
        await batch.commit();
      }

      console.log(`✅ Cleared ${clearedCount} profile pictures`);
      return clearedCount;
    } catch (error) {
      console.error('Error clearing profile pictures:', error);
      return 0;
    }
  }

  /**
   * Delete Cloudinary images (cleanup orphaned images) for an organization
   */
  private async deleteCloudinaryImages(organizationCode: string): Promise<number> {
    try {
      // This is a simplified approach - in production, you'd want to
      // query Cloudinary API for all resources and delete them
      // For now, we'll rely on the deletion during post/message cleanup
      
      // Note: Full Cloudinary cleanup would require:
      // 1. List all resources via Cloudinary Admin API
      // 2. Delete each resource
      // This is resource-intensive, so we'll skip it for now
      // and rely on the cleanup during post/message deletion
      
      console.log('⚠️ Cloudinary image cleanup handled during post/message deletion');
      return 0;
    } catch (error) {
      console.error('Error deleting Cloudinary images:', error);
      return 0;
    }
  }

  /**
   * Extract Cloudinary public_id from URL
   */
  private extractCloudinaryPublicId(url: string): string | null {
    try {
      // Cloudinary URL format: https://res.cloudinary.com/{cloud_name}/image/upload/{public_id}
      const match = url.match(/\/image\/upload\/(?:[^/]+\/)?([^.]+)/);
      if (match && match[1]) {
        return match[1];
      }
      return null;
    } catch (error) {
      console.error('Error extracting Cloudinary public_id:', error);
      return null;
    }
  }
}

export const storageCleanupService = StorageCleanupService.getInstance();
export default StorageCleanupService;

